/**
 * Report Studio, opened on a pipeline report.
 *
 * It used to be a tab of its own, which put "Pipeline" on the tab strip AND
 * in the Create New Report list — two doors to one room, and the tab was the
 * only one that led anywhere. Worse, the tab could only ever show ONE view
 * (whichever the workspace happened to load), while the ledger lists every
 * pipeline report the desk has.
 *
 * So the studio opens the way every other report opens: from its row. A
 * pipeline report is designed in the Studio rather than the field editor
 * because that is where columns, grouping, totals and the print layout live —
 * but reaching it is the same gesture, and the tab is gone.
 */
import { Loader2 } from 'lucide-react'
import { ReportStudio } from '../pipeline-intelligence/ReportStudio'
import { useReportStudioWorkspace } from '../pipeline-intelligence/useReportStudioWorkspace'

export function PipelineReportStudio({
  onClose,
  initialViewId,
}: {
  onClose: () => void
  /** The report to open on — the one just created, or the one whose row was opened. */
  initialViewId?: string | null
}) {
  const studio = useReportStudioWorkspace(initialViewId)

  // The saved views have to be in hand before the Studio can show one — and, when it was asked
  // to open a particular report, that report has to be among them. Said over the page rather
  // than in place of it, because this is a modal opening and a blank screen would read as the
  // click not having registered.
  if (studio.opening) {
    return (
      <div
        className="fixed inset-0 z-[70] flex items-center justify-center gap-2 bg-black/70 text-[12px] text-stone-400"
        data-testid="report-studio-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin" /> Opening Report Studio…
      </div>
    )
  }

  return (
    <>
        {/* Keyed on the report: the Studio seeds its draft (columns, filters, design) when it
            mounts, so a different report has to be a fresh Studio, not the old one re-labelled. */}
        <ReportStudio
          key={studio.view.id}
          view={studio.view}
          allViews={studio.allViews}
          loans={studio.loans}
          demoData={studio.demoData}
          viewsLoading={studio.viewsLoading}
          viewsError={studio.viewsError}
          draftColumns={studio.draftColumns}
          draftFilters={studio.draftFilters}
          draftSort={studio.draftSort}
          assetClasses={studio.assetClasses}
          assignees={studio.assignees}
          years={studio.years}
          saving={studio.saving}
          onSelectView={studio.onSelectView}
          onSaveAsNew={studio.onSaveAsNew}
          onApply={studio.onApply}
          onSave={studio.onSave}
          onRestore={studio.onRestore}
          onPublish={studio.onPublish}
          onUpdateAcl={studio.onUpdateAcl}
          onInvalidated={studio.onInvalidated}
          onClose={onClose}
        />

      {studio.toast && (
        <div className="fixed bottom-4 right-4 z-[75] rounded-md border border-amber-300/20 bg-[#111] px-3 py-2 text-[12px] text-amber-100 shadow-xl">
          {studio.toast}
        </div>
      )}
    </>
  )
}

export default PipelineReportStudio
